import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PORTAL_SOURCE = 'foundlio-portal';
const PREVIEW_SOURCE = 'foundlio-preview';
const PROTOCOL = 'foundlio-preview/v1';

export type ContentPatch = {
  key: string;
  value: string;
  kind?: 'text' | 'src' | 'href';
};

export type PreviewElementRect = {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

export type PreviewElement = {
  key: string;
  kind: 'text' | 'src' | 'href';
  label: string;
  section?: string;
  rect: PreviewElementRect;
};

type PreviewProtocol = 'v1' | 'legacy' | null;

type LegacyOutgoingMessage =
  | { source: typeof PORTAL_SOURCE; type: 'theme'; theme: Record<string, unknown> }
  | { source: typeof PORTAL_SOURCE; type: 'content'; patches: ContentPatch[] }
  | { source: typeof PORTAL_SOURCE; type: 'scrollTo'; key: string };

type V1OutgoingType =
  | 'apply-theme'
  | 'apply-draft-patches'
  | 'scroll-to-element'
  | 'set-selection'
  | 'clear-selection';

type QueuedMessage = {
  v1Type: V1OutgoingType;
  payload?: Record<string, unknown>;
  legacy?: LegacyOutgoingMessage;
};

interface UseSitePreviewOptions {
  previewUrl?: string | null;
  onSectionClick?: (key: string) => void;
  onElementHover?: (element: PreviewElement | null) => void;
  onElementSelected?: (element: PreviewElement) => void;
  onEditRequested?: (element: PreviewElement) => void;
}

function createSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `preview-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function getPreviewOrigin(previewUrl?: string | null): string | null {
  if (!previewUrl || typeof window === 'undefined') return null;
  try {
    return new URL(previewUrl, window.location.href).origin;
  } catch {
    return null;
  }
}

export function buildPreviewUrl(previewUrl: string, sessionId: string): string {
  const url = new URL(previewUrl, window.location.href);
  url.searchParams.set('preview', '1');
  url.searchParams.set('foundlioProtocol', '1');
  url.searchParams.set('foundlioSession', sessionId);
  url.searchParams.set('foundlioParentOrigin', window.location.origin);
  return url.toString();
}

export function isTrustedPreviewEvent(
  event: Pick<MessageEvent, 'origin' | 'source' | 'data'>,
  expectedWindow: Window | null | undefined,
  expectedOrigin: string | null,
  sessionId: string,
): boolean {
  if (!expectedWindow || !expectedOrigin) return false;
  if (event.source !== expectedWindow || event.origin !== expectedOrigin) return false;
  const message = event.data;
  if (!message || typeof message !== 'object' || message.source !== PREVIEW_SOURCE) return false;
  if (message.protocol === PROTOCOL) return message.sessionId === sessionId;
  return !message.protocol;
}

function isPreviewElement(value: unknown): value is PreviewElement {
  if (!value || typeof value !== 'object') return false;
  const element = value as PreviewElement;
  return (
    typeof element.key === 'string' && element.key.length <= 160 &&
    ['text', 'src', 'href'].includes(element.kind) &&
    typeof element.label === 'string' && element.label.length <= 160 &&
    !!element.rect &&
    ['top', 'left', 'right', 'bottom', 'width', 'height'].every(
      (key) => Number.isFinite(element.rect[key as keyof PreviewElementRect]),
    )
  );
}

export function useSitePreview(opts: UseSitePreviewOptions = {}) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const sessionIdRef = useRef(createSessionId());
  const queue = useRef<QueuedMessage[]>([]);
  const [ready, setReady] = useState(false);
  const [protocol, setProtocol] = useState<PreviewProtocol>(null);
  const {
    onSectionClick,
    onElementHover,
    onElementSelected,
    onEditRequested,
  } = opts;
  const previewOrigin = useMemo(() => getPreviewOrigin(opts.previewUrl), [opts.previewUrl]);
  const previewSrc = useMemo(
    () => (opts.previewUrl ? buildPreviewUrl(opts.previewUrl, sessionIdRef.current) : null),
    [opts.previewUrl],
  );

  const sendNow = useCallback((message: QueuedMessage, activeProtocol: PreviewProtocol) => {
    const win = iframeRef.current?.contentWindow;
    if (!win || !previewOrigin || !activeProtocol) return false;
    if (activeProtocol === 'legacy' && message.legacy) {
      win.postMessage(message.legacy, previewOrigin);
      return true;
    }
    win.postMessage({
      source: PORTAL_SOURCE,
      protocol: PROTOCOL,
      sessionId: sessionIdRef.current,
      type: message.v1Type,
      payload: message.payload || {},
    }, previewOrigin);
    return true;
  }, [previewOrigin]);

  const post = useCallback((message: QueuedMessage) => {
    if (!ready || !sendNow(message, protocol)) queue.current.push(message);
  }, [protocol, ready, sendNow]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isTrustedPreviewEvent(
        event,
        iframeRef.current?.contentWindow,
        previewOrigin,
        sessionIdRef.current,
      )) return;

      const message = event.data;
      const isV1 = message.protocol === PROTOCOL;
      const type = message.type;
      const payload = isV1 ? message.payload : message;

      if (type === 'ready') {
        setProtocol(isV1 ? 'v1' : 'legacy');
        setReady(true);
      } else if ((type === 'section-selected' || type === 'section-click') && payload?.key) {
        onSectionClick?.(String(payload.key));
      } else if (type === 'element-hover') {
        onElementHover?.(payload?.element && isPreviewElement(payload.element) ? payload.element : null);
      } else if (type === 'element-selected' && isPreviewElement(payload?.element)) {
        onElementSelected?.(payload.element);
      } else if (type === 'edit-requested' && isPreviewElement(payload?.element)) {
        onEditRequested?.(payload.element);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onEditRequested, onElementHover, onElementSelected, onSectionClick, previewOrigin]);

  useEffect(() => {
    if (!ready || !protocol) return;
    const pending = queue.current;
    queue.current = [];
    pending.forEach((message) => sendNow(message, protocol));
  }, [protocol, ready, sendNow]);

  const sendTheme = useCallback((theme: Record<string, unknown>) => {
    post({
      v1Type: 'apply-theme',
      payload: { theme },
      legacy: { source: PORTAL_SOURCE, type: 'theme', theme },
    });
  }, [post]);

  const sendContent = useCallback((patches: ContentPatch[]) => {
    if (!patches.length) return;
    post({
      v1Type: 'apply-draft-patches',
      payload: { patches },
      legacy: { source: PORTAL_SOURCE, type: 'content', patches },
    });
  }, [post]);

  const scrollTo = useCallback((key: string) => {
    post({
      v1Type: 'scroll-to-element',
      payload: { key },
      legacy: { source: PORTAL_SOURCE, type: 'scrollTo', key },
    });
  }, [post]);

  const setSelection = useCallback((key: string) => {
    post({ v1Type: 'set-selection', payload: { key } });
  }, [post]);

  const clearSelection = useCallback(() => {
    post({ v1Type: 'clear-selection' });
  }, [post]);

  const handleIframeLoad = useCallback(() => {
    setReady(false);
    setProtocol(null);
  }, []);

  return {
    iframeRef,
    previewSrc,
    previewOrigin,
    ready,
    protocol,
    sendTheme,
    sendContent,
    scrollTo,
    setSelection,
    clearSelection,
    handleIframeLoad,
  };
}
